/**
 * Renders an admin-authored record template into a detached DOM tree.
 *
 * Pipeline order is load-bearing:
 *
 *   1. parse      — DOMParser produces an INERT document: no script executes
 *                   and no subresource is fetched, even for markup that
 *                   survives the next step.
 *   2. sanitise   — allowlist filter (templateSanitiser.js), applied while the
 *                   tree is still detached from the live document.
 *   3. render     — a single recursive walk that expands repeats, evaluates
 *                   conditionals, and substitutes {{ }} placeholders.
 *
 * Sanitising BEFORE rendering is deliberate. Rendering first would mean running
 * the sanitiser over attacker-influenced text, so the safety of the output
 * would depend on the sanitiser's completeness. In this order the sanitiser
 * only ever sees the admin's own markup, and record values are neutralised
 * separately by construction (below).
 *
 * Record values are written with `textContent`, never `innerHTML`. That is the
 * XSS boundary that actually matters here: a record whose title is
 * `<img src=x onerror=...>` becomes literal, visible text because textContent
 * does not parse markup. No escaping table is involved, so there is no escaping
 * table to get wrong. This holds inside repeats too — every loop iteration goes
 * through the same text-node path.
 *
 * Attribute values are substituted then RE-CHECKED against the URL allowlist —
 * a record value is untrusted input, so `href="{{ x.link }}"` must not smuggle
 * in `javascript:` after the sanitiser has run. setAttribute takes a string, so
 * a value containing `"><img>` is stored as literal characters, not reparsed.
 *
 * WHY ONE RECURSIVE WALK RATHER THAN SEPARATE PASSES: each repeat iteration
 * needs its own variable scope, so substitution cannot be a single flat pass
 * over the finished tree — by then every clone would share one scope and the
 * loop variable would be ambiguous. Carrying the scope down the recursion is
 * what makes nested repeats (an activity's splits inside a list of activities)
 * resolve correctly.
 */

import { interpolate, hasPlaceholder, resolvePath } from './recordTemplate';
import { sanitiseTemplateTree, isSafeUrl } from './templateSanitiser';

const URL_ATTRS = new Set(['href', 'src']);

const REPEAT_ATTR = 'data-arcee-repeat';
const AS_ATTR = 'data-arcee-as';
const IF_ATTR = 'data-arcee-if';
const EMPTY_ATTR = 'data-arcee-empty';
const ERROR_ATTR = 'data-arcee-error';
const ACTIONS_ATTR = 'data-arcee-actions';
const HTML_ATTR = 'data-arcee-html';

// `{{ actions }}` places EVERY button; `{{ actions.btn_id }}` places one
// specific button, so a template with several buttons can position each of them
// separately rather than getting one undifferentiated row.
//
// Deliberately NOT global: `.test()` on a /g/ regex advances lastIndex, so the
// second identical string in a repeated block would silently fail to match.
const ACTIONS_TOKEN_RE = /\{\{\s*actions(?:\.([A-Za-z0-9_-]+))?\s*\}\}/;

/**
 * Names the loop variable from the list's alias when data-arcee-as is absent.
 *
 * Only the last path segment matters ("activity.splits" → "split"), and only
 * the two English plural forms worth guessing are handled. Anything else is
 * left alone — an author whose alias does not singularise cleanly writes
 * data-arcee-as, which is always exact.
 */
export function singularise(alias) {
  const last = String(alias || '').split('.').pop();
  if (/ies$/i.test(last)) return `${last.slice(0, -3)}y`;
  if (/(ss|us|is)$/i.test(last)) return last;          // "address", "status"
  if (/s$/i.test(last)) return last.slice(0, -1);
  return last;
}

/**
 * Truthiness for data-arcee-if.
 *
 * An empty array is FALSE — "show this block if there are any splits" is the
 * common intent, and JS would otherwise call [] truthy. Empty string, null and
 * undefined are false; 0 and false are false as usual.
 */
function isTruthy(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (value === null || value === undefined || value === '') return false;
  return Boolean(value);
}

function substituteAttributes(el, scope, options) {
  for (const attr of Array.from(el.attributes)) {
    if (!hasPlaceholder(attr.value)) continue;

    const resolved = interpolate(attr.value, scope, options);

    // Re-check after substitution: the record supplied part of this value.
    if (URL_ATTRS.has(attr.name.toLowerCase()) && !isSafeUrl(resolved)) {
      el.removeAttribute(attr.name);
      continue;
    }
    el.setAttribute(attr.name, resolved);
  }
}

/**
 * Expands one repeat container in place.
 *
 * The container's children are the per-item template. They are captured once,
 * the container is emptied, and a fresh clone is rendered per item under a
 * scope extended with the loop variable. Cloning before rendering matters —
 * rendering the original first would bake one item's values into the template
 * every later iteration reuses.
 *
 * An empty (or missing, or failed — Phase 3 sends `[]` for a failed block) list
 * removes the container entirely rather than leaving an empty shell, so a
 * heading above it never floats over blank space.
 */
function expandRepeat(el, scope, failed, options) {
  const alias = el.getAttribute(REPEAT_ATTR);
  const loopVar = el.getAttribute(AS_ATTR) || singularise(alias);

  const resolved = resolvePath(scope, alias);
  const items = Array.isArray(resolved) ? resolved : [];

  const template = Array.from(el.childNodes).map(n => n.cloneNode(true));

  // A failed block and an empty one both have zero rows, but they mean
  // opposite things. Showing "No splits recorded" because the splits table was
  // unreachable tells the visitor something false, so the two states get
  // different siblings and only one of them is ever revealed.
  const didFail = failed.has(String(alias).split('.').pop());

  // Pair the empty/error markers to THIS repeat by walking forward from it and
  // stopping at the next repeat. Searching the whole parent would let two
  // repeats in one container claim each other's markers — the second list's
  // "nothing here" message showing above a first list that has rows.
  //
  // The search also climbs: a repeat is often a <tbody> inside a <table>, and
  // the author's "nothing here" line sits after the TABLE, not after the tbody.
  // Each level is scanned in turn, stopping as soon as markers are found, so a
  // repeat only ever claims markers that follow it.
  let emptyEl = null;
  let errorEl = null;
  for (let node = el; node && !emptyEl && !errorEl; node = node.parentElement) {
    for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
      if (sib.hasAttribute(REPEAT_ATTR)) break;
      if (!emptyEl && sib.hasAttribute(EMPTY_ATTR)) emptyEl = sib;
      if (!errorEl && sib.hasAttribute(ERROR_ATTR)) errorEl = sib;
    }
    if (node.parentElement === null || node.parentElement.nodeName === 'BODY') break;
  }

  if (items.length === 0) {
    if (didFail) {
      // Prefer an explicit error state; fall back to the empty one only when
      // the author wrote no error state at all.
      if (errorEl) {
        errorEl.removeAttribute(ERROR_ATTR);
        emptyEl?.remove();
      } else if (emptyEl) {
        emptyEl.removeAttribute(EMPTY_ATTR);
      }
    } else {
      errorEl?.remove();
      if (emptyEl) emptyEl.removeAttribute(EMPTY_ATTR);
    }
    el.remove();
    return;
  }

  // Rows exist — neither fallback state may show.
  emptyEl?.remove();
  errorEl?.remove();

  el.replaceChildren();
  el.removeAttribute(REPEAT_ATTR);
  el.removeAttribute(AS_ATTR);

  for (const item of items) {
    const itemScope = { ...scope, [loopVar]: item };
    for (const node of template) {
      const clone = node.cloneNode(true);
      // Append BEFORE rendering: a node that replaces itself (the {{ actions }}
      // marker becoming an anchor element) needs a parent to replace itself in.
      // Rendering while detached silently leaves the marker as literal text.
      el.appendChild(clone);
      renderNode(clone, itemScope, failed, options);
    }
  }
}

/**
 * Renders one node under `scope`, recursing into its children.
 *
 * Children are snapshotted before the walk because rendering mutates the tree
 * (repeats replace children, conditionals remove nodes), which would otherwise
 * invalidate a live NodeList mid-iteration.
 */
function renderNode(node, scope, failed, options) {
  if (node.nodeType === Node.TEXT_NODE) {
    // `{{ actions }}` becomes an empty anchor element the caller mounts buttons
    // into. Handled before ordinary substitution so it can never be resolved as
    // a (missing) data path and quietly vanish.
    const actionsMatch = ACTIONS_TOKEN_RE.exec(node.nodeValue || '');
    if (actionsMatch) {
      const anchor = node.ownerDocument.createElement('div');
      // An empty value means "all buttons"; a named one restricts this anchor
      // to a single button id.
      anchor.setAttribute(ACTIONS_ATTR, actionsMatch[1] || '');
      node.replaceWith(anchor);
      return;
    }
    if (hasPlaceholder(node.nodeValue)) {
      // nodeValue on a text node is textContent semantics: never parsed as markup.
      node.nodeValue = interpolate(node.nodeValue, scope, options);
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  // Conditional first — a subtree that is dropped must not be rendered at all.
  if (node.hasAttribute(IF_ATTR)) {
    if (!isTruthy(resolvePath(scope, node.getAttribute(IF_ATTR)))) {
      node.remove();
      return;
    }
    node.removeAttribute(IF_ATTR);
  }

  // Per-record rich content: the named column holds formatting the builder
  // wants rendered, not shown as characters.
  //
  // This is the ONE place a record value becomes markup instead of text, and it
  // is opt-in per element — a builder has to write data-arcee-html deliberately.
  // Every other value stays escaped, so the default is still safe.
  //
  // The stored content is untrusted (it came from a table row), so it goes
  // through the SAME allowlist as the builder's own markup before it is
  // attached: scripts, event handlers and javascript: links are removed. What
  // survives is ordinary formatting — headings, lists, bold, links.
  if (node.hasAttribute(HTML_ATTR)) {
    const value = resolvePath(scope, node.getAttribute(HTML_ATTR));
    node.removeAttribute(HTML_ATTR);
    substituteAttributes(node, scope, options);
    node.replaceChildren();

    if (value !== null && value !== undefined && value !== '') {
      const doc = new DOMParser().parseFromString(`<body>${String(value)}</body>`, 'text/html');
      sanitiseTemplateTree(doc.body);
      while (doc.body.firstChild) {
        node.appendChild(node.ownerDocument.adoptNode(doc.body.firstChild));
      }
    }
    return;
  }

  if (node.hasAttribute(REPEAT_ATTR)) {
    substituteAttributes(node, scope, options);
    expandRepeat(node, scope, failed, options);
    return;
  }

  substituteAttributes(node, scope, options);

  for (const child of Array.from(node.childNodes)) {
    renderNode(child, scope, failed, options);
  }
}

/**
 * @param {string} html - admin-authored markup
 * @param {Object} data - resolved data tree, keyed by table name,
 *                        e.g. { activity: {...}, splits: [...] }
 * @param {Set<string>} [failedSet] - aliases whose related block failed to
 *                        load, so their container shows an "unavailable"
 *                        state rather than an "empty" one
 * @returns {HTMLElement} a detached <div> holding the rendered tree
 */
export function renderRecordTemplate(html, data, failedSet, options) {
  const container = document.createElement('div');
  if (typeof html !== 'string' || html === '') return container;

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;

  sanitiseTemplateTree(body);

  const scope = data || {};
  const failed = failedSet instanceof Set ? failedSet : new Set();

  for (const child of Array.from(body.childNodes)) {
    renderNode(child, scope, failed, options);
  }

  // Markers never claimed by a repeat are inert decoration. An unclaimed ERROR
  // marker is REMOVED rather than revealed — showing "couldn't load" for a
  // block that never failed would be its own kind of false statement.
  body.querySelectorAll(`[${ERROR_ATTR}]`).forEach(el => el.remove());
  body.querySelectorAll(`[${EMPTY_ATTR}]`).forEach(el => el.removeAttribute(EMPTY_ATTR));

  // Adopt into this document so the nodes can be mounted by React.
  while (body.firstChild) {
    container.appendChild(document.adoptNode(body.firstChild));
  }

  return container;
}
